import domtoimage from 'dom-to-image';
import DOMPurify from 'dompurify';
import { marked } from 'marked';
import { computed, inject, ref, watch } from 'vue';
import { useRoute } from 'vue-router';

import { useAIChatState } from '@/composables/mcp/useAIChatState';
import { DEFAULT_SUGGESTED_PROMPTS, SUGGESTED_PROMPTS } from '@/constants/mcp';
import { ProvidedAIChatState } from '@/injectionKeys/global';
import { SentMessage } from '@/stores/MCPWebsocketStore';

export function useMessageBuilder() {
  const aiChatState = inject(ProvidedAIChatState);
  const {
    receivedMessagesInOrder,
    currentMessageEndIndex,
    parsedMessages,
    wsStore,
    isWaitingOnResponse,
    hasReceivedFirstResponse,
    nextMessage,
  } = aiChatState as ReturnType<typeof useAIChatState>;

  const currentChatSession = computed(() => {
    return parsedMessages.value[0]?.chatSessionId || null;
  });

  const currentMessageId = computed(() => {
    return (
      parsedMessages.value[currentMessageEndIndex.value + 1]?.messageId || null
    );
  });

  function simpleMarkdownToHtml(text: string): string {
    const dirtyHtml = marked(text);
    return DOMPurify.sanitize(dirtyHtml, {
      ALLOWED_TAGS: [
        'p',
        'br',
        'strong',
        'em',
        'b',
        'i',
        'a',
        'ul',
        'ol',
        'li',
        'code',
        'pre',
        'h1',
        'h2',
        'h3',
        'h4',
        'hr',
        'blockquote',
      ],
    });
  }

  function buildNextMessage() {
    if (
      !parsedMessages.value.length ||
      !isWaitingOnResponse.value ||
      !currentMessageId.value
    )
      return;

    const currentMessageIndex = parsedMessages.value.length - 1;
    const lastMessage = parsedMessages.value[currentMessageIndex];

    if (!lastMessage) return;

    if (!lastMessage.content.includes('Calling tool:')) {
      if (!hasReceivedFirstResponse.value && lastMessage.content.trim()) {
        hasReceivedFirstResponse.value = true;
      }
      nextMessage.value += lastMessage.content;
    } else console.log(lastMessage.content);

    if (lastMessage.messageComplete) {
      receivedMessagesInOrder.value.push({
        role: 'bot',
        content: simpleMarkdownToHtml(nextMessage.value),
      });
      nextMessage.value = '';
      currentMessageEndIndex.value = currentMessageIndex;
      isWaitingOnResponse.value = false;
    }
  }

  watch(() => parsedMessages.value.length, buildNextMessage, {
    immediate: true,
  });

  // Sending
  const messageToSend = ref('');

  async function captureScreenshotWithDomToImage(): Promise<string> {
    const appContent = document.querySelector('[data-testid="app-content"]');
    if (!appContent) return '';

    try {
      const dataUrl = await domtoimage.toPng(appContent as HTMLElement, {
        quality: 1.0,
        bgcolor: '#ffffff',
        style: {
          transform: 'scale(1)',
          'transform-origin': 'top left',
        },
      });
      return dataUrl;
    } catch (error) {
      console.error('Failed to capture screenshot with dom-to-image:', error);
      return '';
    }
  }

  const sendScreenshot = ref(false);
  async function sendMessage() {
    if (messageToSend.value.trim()) {
      const content = messageToSend.value;
      const current_page = {
        url: route.path,
        params: route.params as Record<string, string>,
      };

      const currentMessage: SentMessage = {
        chatSessionId: currentChatSession.value as string,
        content,
        current_page,
      };
      if (sendScreenshot.value) {
        currentMessage.image = await captureScreenshotWithDomToImage();
      }
      wsStore.sendMessage(currentMessage);
      receivedMessagesInOrder.value.push({ role: 'user', content });
      isWaitingOnResponse.value = true;
      hasReceivedFirstResponse.value = false;
      messageToSend.value = '';
    }
  }

  const formattedNextMessage = computed(() =>
    simpleMarkdownToHtml(nextMessage.value),
  );

  const route = useRoute();

  const suggestedPrompts = computed(() => {
    const suggestedPromptFromRoute = SUGGESTED_PROMPTS.find(
      (prompt) => prompt.path === route.path,
    )?.prompts;
    if (suggestedPromptFromRoute && suggestedPromptFromRoute.length > 0) {
      const shuffled = [...suggestedPromptFromRoute].sort(
        () => Math.random() - 0.5,
      );
      return shuffled.slice(0, 3);
    }
    return DEFAULT_SUGGESTED_PROMPTS;
  });
  function sendSuggestedPrompt(prompt: string) {
    wsStore.sendMessage({
      chatSessionId: null,
      content: prompt,
      current_page: {
        url: route.path,
        params: route.params as Record<string, string>,
      },
    });
    receivedMessagesInOrder.value.push({ role: 'user', content: prompt });
    isWaitingOnResponse.value = true;
  }

  return {
    formattedNextMessage,
    isWaitingOnResponse,
    hasReceivedFirstResponse,
    messageToSend,
    nextMessage,
    receivedMessagesInOrder,
    suggestedPrompts,
    sendMessage,
    sendScreenshot,
    sendSuggestedPrompt,
    wsStore,
  };
}
